/**
 * Timestamp formatting for API `Instant` fields.
 *
 * The backend serializes `java.time.Instant` (generatedAt, dueAt, completedAt, nextGenerationAt,
 * etc.) as an epoch value in SECONDS with a fractional part (e.g. 1789511454.278613) — NOT
 * milliseconds and NOT an ISO string. Passing that straight to `new Date(value)` treats it as
 * milliseconds and yields a 1970 date. These helpers detect the format and scale correctly.
 */

/** Parse an API timestamp (epoch seconds, numeric string, or ISO) to a Date, or null. */
export function parseApiInstant(value: unknown): Date | null {
  if (value == null || value === '') return null;

  let date: Date;
  if (typeof value === 'number') {
    // Epoch seconds → ms when the magnitude is too small to be ms.
    date = new Date(value < 1e12 ? value * 1000 : value);
  } else if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
    const num = Number(value);
    date = new Date(num < 1e12 ? num * 1000 : num);
  } else {
    date = new Date(value as string);
  }

  return isNaN(date.getTime()) ? null : date;
}

/** Format an API timestamp as a locale date (e.g. "Sep 25, 2026"), or a placeholder. */
export function formatApiDate(value: unknown, placeholder = '—'): string {
  const d = parseApiInstant(value);
  return d
    ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : placeholder;
}

/** Format an API timestamp as a locale date+time, or a placeholder. */
export function formatApiDateTime(value: unknown, placeholder = '—'): string {
  const d = parseApiInstant(value);
  return d ? d.toLocaleString() : placeholder;
}
